import { useState, useEffect, useRef, type ChangeEvent, type ReactNode } from 'react';
import {
  User, Building2, MessageCircle, Plug, Palette, Shield,
  Calendar, CreditCard, ChevronDown,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { SemmyAvatar } from '@/components/SemmyAvatar';

// ── Default templates ─────────────────────────────────────────────────────────

const DEFAULT_WHATSAPP_TEMPLATE =
  'Hoi {voornaam}! Je hebt je aangemeld voor een offerte. ' +
  'Om je een goede prijs te geven heb ik nog een paar details nodig — ' +
  'kost je 60 seconden: {link}';

const DEFAULT_EMAIL_SUBJECT = 'Je offerte aanvraag — nog één stap';

const DEFAULT_EMAIL_BODY =
  'Hoi {voornaam}, bedankt voor je interesse. Om je een scherpe ' +
  'offerte te sturen heb ik nog een paar gegevens nodig. Dat invullen kost ' +
  'je 60 seconden.';

const DEFAULT_FOLLOWUP_TEMPLATE =
  'Hoi {voornaam}, je offerte aanvraag stond nog open. Nog interesse? {link}';

// ── Shared field components ───────────────────────────────────────────────────

function Field({ label, helper, children }: { label: string; helper?: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
        {label}
      </label>
      {children}
      {helper && <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5 }}>{helper}</p>}
    </div>
  );
}

const INP_STYLE: React.CSSProperties = {
  width: '100%', height: 42, boxSizing: 'border-box',
  padding: '0 14px', borderRadius: 9,
  background: 'var(--bg-input)',
  border: '1px solid var(--border-primary)',
  color: 'var(--text-primary)', fontSize: 13, outline: 'none',
};

function TextInput({ value, onChange, placeholder, disabled }: {
  value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean;
}) {
  return (
    <input
      type="text"
      style={{ ...INP_STYLE, opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'text' }}
      value={value}
      onChange={disabled ? undefined : e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function TextArea({ value, onChange, rows = 4, placeholder, disabled }: {
  value: string; onChange?: (v: string) => void; rows?: number; placeholder?: string; disabled?: boolean;
}) {
  return (
    <textarea
      style={{ ...INP_STYLE, height: 'auto', padding: '10px 14px', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5', opacity: disabled ? 0.6 : 1 }}
      rows={rows}
      value={value}
      onChange={disabled ? undefined : e => onChange?.(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function SaveButton({ saving, saved, onClick, disabled }: {
  saving: boolean; saved: boolean; onClick: () => void; disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
      <button
        onClick={onClick}
        disabled={saving || disabled}
        style={{
          height: 40, padding: '0 20px', borderRadius: 10,
          background: saving || disabled ? 'var(--bg-input)' : '#fff',
          color: saving || disabled ? 'var(--text-muted)' : '#0a0a0a',
          border: 'none', fontSize: 13, fontWeight: 600, cursor: saving || disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {saving ? 'Opslaan…' : 'Opslaan'}
      </button>
      {saved && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Opgeslagen</span>}
    </div>
  );
}

// ── Accordion section ─────────────────────────────────────────────────────────

function AccSection({
  icon, iconBg, iconColor, title, subtitle, defaultOpen = false, children,
}: {
  icon: ReactNode; iconBg?: string; iconColor?: string;
  title: string; subtitle?: string; defaultOpen?: boolean; children?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      background: 'var(--bg-surface-raised)',
      border: '1px solid var(--border-separator)',
      borderRadius: 12, marginBottom: 14, overflow: 'hidden',
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 18px', cursor: 'pointer',
          borderBottom: open ? '1px solid var(--border-separator)' : 'none',
          userSelect: 'none',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 9, flexShrink: 0,
          background: iconBg || 'var(--bg-input)',
          color: iconColor || 'var(--text-tertiary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>{subtitle}</div>}
        </div>
        <ChevronDown
          size={15}
          style={{
            color: 'var(--text-muted)', marginLeft: 'auto', flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
          }}
        />
      </div>
      {open && <div style={{ padding: '4px 18px 20px' }}>{children}</div>}
    </div>
  );
}

// ── Integration row ───────────────────────────────────────────────────────────

function IntegrationRow({ icon, iconBg, iconColor, name, description, connected = false, onConnect, comingSoon = false }: {
  icon: ReactNode; iconBg: string; iconColor: string;
  name: string; description: string; connected?: boolean; onConnect?: () => void; comingSoon?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '13px 0', borderTop: '1px solid var(--border-separator)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8, flexShrink: 0,
          background: iconBg, color: iconColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{description}</div>
        </div>
      </div>
      {connected ? (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 34, padding: '0 14px', borderRadius: 9,
          background: 'var(--success-bg)', color: 'var(--success-text)',
          fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success-text)' }} />
          Verbonden
        </div>
      ) : comingSoon ? (
        <div style={{
          height: 34, padding: '0 14px', borderRadius: 9, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center',
          background: 'var(--bg-input)', border: '1px solid var(--border-separator)',
          color: 'var(--text-muted)', fontSize: 11, fontWeight: 500,
        }}>
          Binnenkort
        </div>
      ) : (
        <button
          onClick={onConnect}
          style={{
            height: 34, padding: '0 16px', borderRadius: 9, flexShrink: 0,
            background: 'var(--bg-elevated, var(--bg-input))',
            border: '1px solid var(--border-primary)',
            color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Verbinden
        </button>
      )}
    </div>
  );
}

// ── Main settings page ────────────────────────────────────────────────────────

export default function PartnerSettingsPage() {
  const { effectivePartnerId, effectiveRole } = useAuth();
  const { theme, setTheme } = useTheme();
  const canEditCompany = effectiveRole === 'partner_principal';
  const [loading, setLoading] = useState(true);

  // ── Personal ─────────────────────────────────────────────────────────────────
  const [profile, setProfile] = useState({ voornaam: '', achternaam: '', email: '', telefoonnummer: '' });
  const [profileInit, setProfileInit] = useState({ voornaam: '', achternaam: '', email: '', telefoonnummer: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedProfile,  setSavedProfile]  = useState(false);

  // ── Company ──────────────────────────────────────────────────────────────────
  const [company, setCompany] = useState({
    company_name: '', kvk_number: '', btw_number: '', iban: '',
    company_address: '', company_postcode: '', company_city: '',
  });
  const [companyInit, setCompanyInit] = useState({
    company_name: '', kvk_number: '', btw_number: '', iban: '',
    company_address: '', company_postcode: '', company_city: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [savedCompany,  setSavedCompany]  = useState(false);

  // ── Online presence (inside Bedrijfsgegevens) ─────────────────────────────────
  const [online, setOnline] = useState({
    website_url: '', whatsapp_number: '', gallery_url: '', instagram_url: '',
    email_about_text: '', email_tagline: '',
  });
  const [onlineInit, setOnlineInit] = useState({
    website_url: '', whatsapp_number: '', gallery_url: '', instagram_url: '',
    email_about_text: '', email_tagline: '',
  });
  const [savingOnline, setSavingOnline] = useState(false);
  const [savedOnline,  setSavedOnline]  = useState(false);

  // ── Semmy ────────────────────────────────────────────────────────────────────
  const [comms, setComms] = useState({
    sender_name: '', sender_language: 'nl', sender_signature: '', sender_phone: '',
    semmy_avatar_url: '',
    welcome_email_subject: '', welcome_email_body: '',
    followup_whatsapp_template: '', followup_enabled: true,
  });
  const [commsInit, setCommsInit] = useState({
    sender_name: '', sender_language: 'nl', sender_signature: '', sender_phone: '',
    semmy_avatar_url: '',
    welcome_email_subject: '', welcome_email_body: '',
    followup_whatsapp_template: '', followup_enabled: true,
  });
  const [semmyAvatarUrl,    setSemmyAvatarUrl]    = useState<string | null>(null);
  const [uploadingAvatar,   setUploadingAvatar]   = useState(false);
  const [profilePhotoUrl,   setProfilePhotoUrl]   = useState<string | null>(null);
  const [uploadingProfile,  setUploadingProfile]  = useState(false);
  const profileFileRef = useRef<HTMLInputElement>(null);
  const [savingComms,     setSavingComms]     = useState(false);
  const [savedComms,      setSavedComms]      = useState(false);

  // ── Load ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!effectivePartnerId) return;
    (async () => {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const meta = user.user_metadata as Record<string, unknown>;
        const fullName = (meta?.full_name as string) || user.email || '';
        const parts = fullName.split(' ');
        const p = {
          voornaam: parts[0] || '',
          achternaam: parts.slice(1).join(' ') || '',
          email: user.email || '',
          telefoonnummer: (meta?.phone as string) || '',
        };
        setProfile(p); setProfileInit(p);
        if (meta?.avatar_url) setProfilePhotoUrl(meta.avatar_url as string);
      }

      const { data } = await supabase
        .from('semmy_operator_context')
        .select('*')
        .eq('partner_id', effectivePartnerId)
        .maybeSingle();

      if (data) {
        const d = data as Record<string, unknown>;
        const co = {
          company_name:     (d.company_name     as string) || '',
          kvk_number:       (d.kvk_number       as string) || '',
          btw_number:       (d.btw_number       as string) || '',
          iban:             (d.iban             as string) || '',
          company_address:  (d.company_address  as string) || '',
          company_postcode: (d.company_postcode as string) || '',
          company_city:     (d.company_city     as string) || '',
        };
        const on = {
          website_url:      (d.website_url      as string) || '',
          whatsapp_number:  (d.whatsapp_number  as string) || '',
          gallery_url:      (d.gallery_url      as string) || '',
          instagram_url:    (d.instagram_url    as string) || '',
          email_about_text: (d.email_about_text as string) || '',
          email_tagline:    (d.email_tagline    as string) || '',
        };
        const cm = {
          sender_name:      (d.sender_name      as string) || '',
          sender_language:  (d.sender_language  as string) || 'nl',
          sender_signature: (d.sender_signature as string) || '',
          sender_phone:     (d.sender_phone     as string) || '',
          semmy_avatar_url: (d.semmy_avatar_url as string) || '',
          welcome_email_subject:      (d.welcome_email_subject      as string) || '',
          welcome_email_body:         (d.welcome_email_body         as string) || '',
          followup_whatsapp_template: (d.followup_whatsapp_template as string) || '',
          followup_enabled: d.followup_enabled !== false,
        };
        setCompany(co);  setCompanyInit(co);
        setOnline(on);   setOnlineInit(on);
        setComms(cm);    setCommsInit(cm);
        setSemmyAvatarUrl((d.semmy_avatar_url as string) || null);
      }

      setLoading(false);
    })();
  }, [effectivePartnerId]);

  async function upsert(fields: Record<string, unknown>) {
    await supabase
      .from('semmy_operator_context')
      .upsert({ partner_id: effectivePartnerId, ...fields }, { onConflict: 'partner_id' });
  }

  // ── Dirty flags ──────────────────────────────────────────────────────────────
  const profileDirty = JSON.stringify(profile) !== JSON.stringify(profileInit);
  const companyDirty = canEditCompany && JSON.stringify(company) !== JSON.stringify(companyInit);
  const onlineDirty  = JSON.stringify(online)  !== JSON.stringify(onlineInit);
  const commsDirty   = JSON.stringify(comms)   !== JSON.stringify(commsInit);
  const anyDirty     = profileDirty || companyDirty || onlineDirty || commsDirty;

  // ── Save functions ────────────────────────────────────────────────────────────
  async function saveProfileSection() {
    setSavingProfile(true);
    await upsert({
      sender_name:  `${profile.voornaam} ${profile.achternaam}`.trim(),
      sender_phone: profile.telefoonnummer,
      sender_email: profile.email,
    });
    setSavingProfile(false); setSavedProfile(true);
    setProfileInit(profile);
    setTimeout(() => setSavedProfile(false), 2000);
  }

  async function saveCompanySection() {
    setSavingCompany(true);
    await upsert(company);
    setSavingCompany(false); setSavedCompany(true);
    setCompanyInit(company);
    setTimeout(() => setSavedCompany(false), 2000);
  }

  async function saveOnlineSection() {
    setSavingOnline(true);
    await upsert(online);
    setSavingOnline(false); setSavedOnline(true);
    setOnlineInit(online);
    setTimeout(() => setSavedOnline(false), 2000);
  }

  async function saveCommsSection() {
    setSavingComms(true);
    await upsert({
      sender_name:      comms.sender_name,
      sender_language:  comms.sender_language,
      sender_signature: comms.sender_signature,
      sender_phone:     comms.sender_phone || null,
      welcome_email_subject:      comms.welcome_email_subject || null,
      welcome_email_body:         comms.welcome_email_body || null,
      followup_whatsapp_template: comms.followup_whatsapp_template || null,
      followup_enabled:           comms.followup_enabled,
    });
    setSavingComms(false); setSavedComms(true);
    setCommsInit(comms);
    setTimeout(() => setSavedComms(false), 2000);
  }

  async function saveAllDirty() {
    const tasks: Promise<void>[] = [];
    if (profileDirty) tasks.push(saveProfileSection());
    if (companyDirty) tasks.push(saveCompanySection());
    if (onlineDirty)  tasks.push(saveOnlineSection());
    if (commsDirty)   tasks.push(saveCommsSection());
    await Promise.all(tasks);
  }

  const handleAvatarFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !effectivePartnerId) return;
    setUploadingAvatar(true);
    const ext = file.name.split('.').pop() || 'png';
    const path = `${effectivePartnerId}/semmy-avatar.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('semmy-avatars')
      .upload(path, file, { upsert: true });
    if (!uploadError) {
      const { data } = supabase.storage.from('semmy-avatars').getPublicUrl(path);
      if (data.publicUrl) {
        await upsert({ semmy_avatar_url: data.publicUrl });
        setSemmyAvatarUrl(data.publicUrl);
        setComms(s => ({ ...s, semmy_avatar_url: data.publicUrl }));
      }
    }
    setUploadingAvatar(false);
  };

  const handleProfilePhotoFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !effectivePartnerId) return;
    setUploadingProfile(true);
    const ext = file.name.split('.').pop() || 'png';
    const path = `${effectivePartnerId}/profile-photo.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('semmy-avatars')
      .upload(path, file, { upsert: true });
    if (!uploadError) {
      const { data } = supabase.storage.from('semmy-avatars').getPublicUrl(path);
      if (data.publicUrl) {
        await supabase.auth.updateUser({ data: { avatar_url: data.publicUrl } });
        setProfilePhotoUrl(data.publicUrl);
      }
    }
    setUploadingProfile(false);
    if (e.target) e.target.value = '';
  };

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 rounded-full border-2" style={{ borderColor: 'var(--border-primary)', borderTopColor: 'var(--accent)', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  const initials = ((profile.voornaam[0] || '') + (profile.achternaam[0] || '')).toUpperCase() || '?';

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 560 }}>

      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>Instellingen</h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Je gegevens, koppelingen en hoe Semmy voor je communiceert.</p>
      </div>

      {/* Unsaved changes banner */}
      {anyDirty && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderRadius: 10, gap: 12, marginBottom: 14,
          background: 'var(--accent-dim)', border: '1px solid var(--accent-glow)',
        }}>
          <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>Je hebt niet-opgeslagen wijzigingen</span>
          <button
            onClick={saveAllDirty}
            style={{
              fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 7,
              background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}
          >
            Alles opslaan
          </button>
        </div>
      )}

      {/* ── Mijn gegevens ── */}
      <AccSection
        icon={<User size={17} />}
        iconBg="rgba(113,180,112,0.12)"
        iconColor="#71b470"
        title="Mijn gegevens"
        subtitle="Profiel, naam, contact"
        defaultOpen
      >
        {/* Profile photo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 14 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', overflow: 'hidden',
              background: 'rgba(113,180,112,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 600, color: '#71b470',
            }}>
              {profilePhotoUrl
                ? <img src={profilePhotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>
              Profielfoto
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input ref={profileFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfilePhotoFile} disabled={uploadingProfile} />
              <button
                style={{
                  height: 34, padding: '0 16px', borderRadius: 9, fontSize: 12, fontWeight: 600,
                  background: 'var(--bg-input)', border: '1px solid var(--border-primary)',
                  color: 'var(--text-secondary)', cursor: uploadingProfile ? 'not-allowed' : 'pointer',
                  opacity: uploadingProfile ? 0.6 : 1,
                }}
                onClick={() => profileFileRef.current?.click()}
                disabled={uploadingProfile}
              >
                {uploadingProfile ? 'Uploaden…' : 'Uploaden'}
              </button>
            </div>
          </div>
        </div>

        {/* Name */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 18 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Voornaam
            </label>
            <input style={INP_STYLE} value={profile.voornaam} onChange={e => setProfile(s => ({ ...s, voornaam: e.target.value }))} placeholder="bijv. Jan" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Achternaam
            </label>
            <input style={INP_STYLE} value={profile.achternaam} onChange={e => setProfile(s => ({ ...s, achternaam: e.target.value }))} placeholder="bijv. de Vries" />
          </div>
        </div>

        <Field label="E-mailadres">
          <TextInput value={profile.email} onChange={v => setProfile(s => ({ ...s, email: v }))} placeholder="bijv. jan@bedrijf.nl" />
        </Field>
        <Field label="Telefoonnummer" helper="Dit nummer gebruikt Semmy als terugbelnummer in berichten.">
          <TextInput value={profile.telefoonnummer} onChange={v => setProfile(s => ({ ...s, telefoonnummer: v }))} placeholder="bijv. 020 308 6259" />
        </Field>

        <SaveButton saving={savingProfile} saved={savedProfile} onClick={saveProfileSection} />
      </AccSection>

      {/* ── Bedrijfsgegevens ── */}
      <AccSection
        icon={<Building2 size={17} />}
        title="Bedrijfsgegevens"
        subtitle="Bedrijfsnaam, KvK, BTW"
      >
        <Field label="Bedrijfsnaam">
          <TextInput value={company.company_name} onChange={v => setCompany(s => ({ ...s, company_name: v }))} placeholder="bijv. Vloer Nederland B.V." disabled={!canEditCompany} />
        </Field>
        <Field label="KvK-nummer" helper="Wordt vermeld op offertes en facturen.">
          <TextInput value={company.kvk_number} onChange={v => setCompany(s => ({ ...s, kvk_number: v }))} placeholder="bijv. 12345678" disabled={!canEditCompany} />
        </Field>
        <Field label="BTW-nummer">
          <TextInput value={company.btw_number} onChange={v => setCompany(s => ({ ...s, btw_number: v }))} placeholder="bijv. NL123456789B01" disabled={!canEditCompany} />
        </Field>
        <Field label="IBAN">
          <TextInput value={company.iban} onChange={v => setCompany(s => ({ ...s, iban: v }))} placeholder="bijv. NL91ABNA0417164300" disabled={!canEditCompany} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>Postcode</label>
            <input style={{ ...INP_STYLE, opacity: !canEditCompany ? 0.6 : 1 }} value={company.company_postcode} onChange={e => canEditCompany && setCompany(s => ({ ...s, company_postcode: e.target.value }))} placeholder="bijv. 1234 AB" disabled={!canEditCompany} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>Stad</label>
            <input style={{ ...INP_STYLE, opacity: !canEditCompany ? 0.6 : 1 }} value={company.company_city} onChange={e => canEditCompany && setCompany(s => ({ ...s, company_city: e.target.value }))} placeholder="bijv. Amsterdam" disabled={!canEditCompany} />
          </div>
        </div>
        <Field label="Adres">
          <TextInput value={company.company_address} onChange={v => setCompany(s => ({ ...s, company_address: v }))} placeholder="bijv. Hoofdstraat 1" disabled={!canEditCompany} />
        </Field>

        {/* Online presence sub-fields */}
        <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--border-separator)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>Online aanwezigheid</div>
        </div>
        <Field label="Website URL">
          <TextInput value={online.website_url} onChange={v => setOnline(s => ({ ...s, website_url: v }))} placeholder="https://pvcvloer.nl" />
        </Field>
        <Field label="Instagram">
          <TextInput value={online.instagram_url} onChange={v => setOnline(s => ({ ...s, instagram_url: v }))} placeholder="https://instagram.com/pvcvloer" />
        </Field>
        <Field label="Galerij / portfolio URL">
          <TextInput value={online.gallery_url} onChange={v => setOnline(s => ({ ...s, gallery_url: v }))} placeholder="https://pvcvloer.nl/galerij" />
        </Field>

        {!canEditCompany ? (
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 14 }}>Alleen de accounthouder kan bedrijfsgegevens wijzigen.</p>
        ) : (
          <div style={{ display: 'flex', gap: 12, marginTop: 18, alignItems: 'center' }}>
            <button
              onClick={async () => { await saveCompanySection(); await saveOnlineSection(); }}
              disabled={savingCompany || savingOnline}
              style={{
                height: 40, padding: '0 20px', borderRadius: 10,
                background: (savingCompany || savingOnline) ? 'var(--bg-input)' : '#fff',
                color: (savingCompany || savingOnline) ? 'var(--text-muted)' : '#0a0a0a',
                border: 'none', fontSize: 13, fontWeight: 600, cursor: (savingCompany || savingOnline) ? 'not-allowed' : 'pointer',
              }}
            >
              {(savingCompany || savingOnline) ? 'Opslaan…' : 'Opslaan'}
            </button>
            {(savedCompany || savedOnline) && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Opgeslagen</span>}
          </div>
        )}
      </AccSection>

      {/* ── Semmy ── */}
      <AccSection
        icon={<MessageCircle size={17} />}
        title="Semmy"
        subtitle="Toon, berichten, werkgebied"
      >
        {/* Semmy avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14 }}>
          <SemmyAvatar size={48} imageUrl={semmyAvatarUrl} showOnlineDot={false} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
              Semmy avatar
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', height: 32, padding: '0 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)', cursor: uploadingAvatar ? 'not-allowed' : 'pointer', opacity: uploadingAvatar ? 0.6 : 1 }}>
              {uploadingAvatar ? 'Uploaden…' : 'Uploaden'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarFile} disabled={uploadingAvatar} />
            </label>
          </div>
        </div>

        <Field label="Naam van Semmy" helper="De naam die Semmy gebruikt in berichten (bijv. 'Ik ben Semmy van …').">
          <TextInput value={comms.sender_name} onChange={v => setComms(s => ({ ...s, sender_name: v }))} placeholder="bijv. Semmy van PVC Vloer Nederland" />
        </Field>
        <Field label="Terugbelnummer" helper="Semmy stuurt dit als contactnummer in berichten.">
          <TextInput value={comms.sender_phone} onChange={v => setComms(s => ({ ...s, sender_phone: v }))} placeholder="bijv. 020 308 6259" />
        </Field>

        {/* Online presence for Semmy context */}
        <Field label="WhatsApp nummer" helper="Klanten kunnen direct appen via welkomst-e-mails.">
          <TextInput value={online.whatsapp_number} onChange={v => setOnline(s => ({ ...s, whatsapp_number: v }))} placeholder="+31612345678" />
        </Field>
        <Field label="Over ons tekst" helper="Verschijnt in welkomst-e-mails van Semmy.">
          <TextArea rows={3} value={online.email_about_text} onChange={v => setOnline(s => ({ ...s, email_about_text: v }))} placeholder="Al 10 jaar leggen wij PVC vloeren in Amsterdam en omgeving..." />
        </Field>

        {/* Message templates */}
        <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--border-separator)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>Automatische berichten</div>
        </div>
        <Field label="E-mail onderwerpregel">
          <TextInput value={comms.welcome_email_subject || DEFAULT_EMAIL_SUBJECT} onChange={v => setComms(s => ({ ...s, welcome_email_subject: v }))} placeholder={DEFAULT_EMAIL_SUBJECT} />
        </Field>
        <Field label="E-mail berichttekst">
          <TextArea rows={3} value={comms.welcome_email_body || DEFAULT_EMAIL_BODY} onChange={v => setComms(s => ({ ...s, welcome_email_body: v }))} placeholder={DEFAULT_EMAIL_BODY} />
        </Field>

        {/* Follow-up toggle */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <button
              role="switch"
              aria-checked={comms.followup_enabled}
              onClick={() => setComms(s => ({ ...s, followup_enabled: !s.followup_enabled }))}
              style={{
                width: 36, height: 20, borderRadius: 10, position: 'relative', cursor: 'pointer', flexShrink: 0,
                background: comms.followup_enabled ? 'var(--accent)' : 'var(--bg-input)',
                border: `1px solid ${comms.followup_enabled ? 'var(--accent)' : 'var(--border-primary)'}`,
                transition: 'background 0.2s',
              }}
            >
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: comms.followup_enabled ? 18 : 2, transition: 'left 0.2s' }} />
            </button>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
              Stuur herinnering als formulier na 24 uur niet is afgemaakt
            </label>
          </div>
          {comms.followup_enabled && (
            <TextArea rows={3} value={comms.followup_whatsapp_template || DEFAULT_FOLLOWUP_TEMPLATE} onChange={v => setComms(s => ({ ...s, followup_whatsapp_template: v }))} placeholder={DEFAULT_FOLLOWUP_TEMPLATE} />
          )}
        </div>

        <SaveButton saving={savingComms} saved={savedComms} onClick={async () => { await saveOnlineSection(); await saveCommsSection(); }} />

        {/* Re-init link */}
        <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--border-separator)', textAlign: 'center' }}>
          <button
            onClick={() => {
              if (!effectivePartnerId) return;
              localStorage.setItem(`onboarding_requested_${effectivePartnerId}`, '1');
              window.location.reload();
            }}
            style={{ fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
          >
            ↺ Semmy opnieuw instellen
          </button>
        </div>
      </AccSection>

      {/* ── Koppelingen ── */}
      <AccSection
        icon={<Plug size={17} />}
        iconBg="rgba(113,180,112,0.12)"
        iconColor="#71b470"
        title="Koppelingen"
        subtitle="Verbind je eigen accounts"
        defaultOpen
      >
        <IntegrationRow
          icon={<Calendar size={16} />}
          iconBg="rgba(66,133,244,0.12)"
          iconColor="#4285f4"
          name="Google Agenda"
          description="Nodig voor de agenda-functie"
          comingSoon
        />
        <IntegrationRow
          icon={<CreditCard size={16} />}
          iconBg="rgba(99,91,255,0.12)"
          iconColor="#635bff"
          name="Stripe"
          description="Voor facturatie, wanneer je klaar bent"
          comingSoon
        />
      </AccSection>

      {/* ── Weergave ── */}
      <AccSection
        icon={<Palette size={17} />}
        title="Weergave"
        subtitle="Achtergrond, thema"
      >
        <div style={{ marginTop: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 10 }}>
            Thema
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['dark', 'light', 'system'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 9, fontSize: 12, fontWeight: 500,
                  background: theme === t ? 'var(--accent)' : 'var(--bg-input)',
                  color: theme === t ? '#fff' : 'var(--text-secondary)',
                  border: `1px solid ${theme === t ? 'var(--accent)' : 'var(--border-primary)'}`,
                  cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                }}
              >
                {t === 'dark' ? 'Donker' : t === 'light' ? 'Licht' : 'Systeem'}
              </button>
            ))}
          </div>
        </div>
      </AccSection>

      {/* ── Footer note ── */}
      <div style={{
        marginTop: 18, padding: '14px 16px',
        border: '1px dashed var(--border-separator)',
        borderRadius: 10, display: 'flex', alignItems: 'center', gap: 11,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          background: 'rgba(113,180,112,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Shield size={15} style={{ color: '#71b470' }} />
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
          Advertenties en leadbronnen worden centraal beheerd door Converze. Je hoeft zelf geen Meta- of advertentieaccount te koppelen.
        </p>
      </div>

    </div>
  );
}
